// src/pages/AdminCategoryList.js

import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import './AdminModals.css'; // Reutilizando o mesmo CSS do modal

const AdminCategoryList = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Estados para o modal (atualizados para a nova estrutura) ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formData, setFormData] = useState({
        label: '',
        slug: '',
        descricao: '',
        coverImage: ''
    });
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Função para gerar slug (boa prática para consistência)
    const generateSlug = (title) => {
        return title.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
    };

    // Efeito para gerar o slug automaticamente a partir do label no modal
    useEffect(() => {
        if (formData.label && !currentCategory) { // Gera slug apenas ao criar uma nova
            setFormData(prev => ({ ...prev, slug: generateSlug(prev.label) }));
        }
    }, [formData.label, currentCategory]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const categoriesCollectionRef = collection(db, 'blog_categorias');
            const data = await getDocs(categoriesCollectionRef);
            const categoriesData = data.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(categoriesData);
            setError(null);
        } catch (err) {
            console.error("Erro ao buscar categorias:", err);
            setError("Falha ao carregar as categorias.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (category = null) => {
        setCurrentCategory(category);
        if (category) {
            setFormData({
                label: category.label || '',
                slug: category.slug || '',
                descricao: category.descricao || '',
                coverImage: category.coverImage || ''
            });
        } else {
            setFormData({ label: '', slug: '', descricao: '', coverImage: '' });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentCategory(null);
        setFormData({ label: '', slug: '', descricao: '', coverImage: '' });
        setIsUploading(false);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const currentSlug = formData.slug || generateSlug(formData.label);
        if (!currentSlug) {
            alert("Por favor, preencha o nome da categoria antes de enviar a imagem.");
            return;
        }

        setIsUploading(true);
        const storageRef = ref(storage, `blog/categorias/${currentSlug}/${file.name}`);
        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            setFormData(prev => ({ ...prev, coverImage: downloadURL }));
        } catch (error) {
            console.error("Erro no upload da imagem:", error);
            alert("Ocorreu um erro ao enviar a imagem.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        if (!formData.label || !formData.slug) {
            alert("Por favor, preencha o Nome e o Slug da categoria.");
            return;
        }

        try {
            if (currentCategory) {
                // Atualizar categoria existente
                const categoryDoc = doc(db, 'blog_categorias', currentCategory.id);
                await updateDoc(categoryDoc, formData);
            } else {
                // Criar nova categoria com valores padrão
                await addDoc(collection(db, 'blog_categorias'), {
                    ...formData,
                    language: 'pt-BR',
                    postCount: 0,
                    posts: []
                });
            }
            fetchCategories();
            handleCloseModal();
        } catch (err) {
            console.error("Erro ao salvar categoria:", err);
            alert("Ocorreu um erro ao salvar a categoria.");
        }
    };

    const handleDeleteCategory = async (category) => {
        // Trava de segurança
        if (category.postCount > 0) {
            alert(`Não é possível apagar a categoria "${category.label}", pois ela contém ${category.postCount} post(s).`);
            return;
        }

        if (window.confirm(`Tem certeza que deseja apagar a categoria "${category.label}"?`)) {
            try {
                const categoryDoc = doc(db, 'blog_categorias', category.id);
                await deleteDoc(categoryDoc);
                fetchCategories();
            } catch (err) {
                console.error("Erro ao apagar categoria:", err);
                alert("Ocorreu um erro ao apagar a categoria.");
            }
        }
    };

    if (loading) return <div className="loading-message">A carregar categorias...</div>;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="admin-list-container">
            <div className="admin-list-header">
                <h1>Gerenciamento de Categorias</h1>
                <button onClick={() => handleOpenModal()} className="create-button">
                    Criar nova categoria
                </button>
            </div>

            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>Nome da Categoria</th>
                            <th>Nº de Posts</th>
                            <th>Slug (URL)</th>
                            <th style={{ textAlign: 'right' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map(cat => (
                            <tr key={cat.id}>
                                <td>{cat.label}</td>
                                <td>{cat.postCount || 0}</td>
                                <td>/blog/categoria/{cat.slug}</td>
                                <td className="actions-cell">
                                    <button onClick={() => handleOpenModal(cat)} className="action-button edit-button">Editar</button>
                                    <button onClick={() => handleDeleteCategory(cat)} className="action-button delete-button">Apagar</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>{currentCategory ? 'Editar Categoria' : 'Nova Categoria'}</h2>
                        <form onSubmit={handleSaveCategory}>
                            <div className="form-group">
                                <label htmlFor="label">Nome da Categoria (Label)</label>
                                <input type="text" id="label" name="label" value={formData.label} onChange={handleFormChange} required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="slug">Slug</label>
                                <input type="text" id="slug" name="slug" value={formData.slug} onChange={handleFormChange} required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="descricao">Descrição</label>
                                <textarea id="descricao" name="descricao" rows="3" value={formData.descricao} onChange={handleFormChange}></textarea>
                            </div>
                            <div className="form-group">
                                <label>Imagem de Capa</label>
                                <input type="file" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} accept="image/*" />
                                <button type="button" className="upload-button" onClick={() => fileInputRef.current.click()} disabled={isUploading}>
                                    {isUploading ? 'A enviar...' : 'Escolher Imagem'}
                                </button>
                                {formData.coverImage && <img src={formData.coverImage} alt="Preview" className="image-preview" />}
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={handleCloseModal} className="button-cancel">Cancelar</button>
                                <button type="submit" className="button-save" disabled={isUploading}>
                                    {isUploading ? 'Aguarde o upload...' : 'Salvar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCategoryList;